import { useState } from "react";
import { toast } from "react-toastify";
import Loader from "../../components/Loader";
import { useNavigate } from "react-router-dom";
import Calling from "../../api/Calling";
import { FiShield, FiArrowRight, FiLock } from "react-icons/fi";
import img2 from '../../Image/logo.svg';

export default function LoginOtp({ onLogin }) {
    const [loader, setLoader] = useState(false);
    const [otp, setOtp] = useState("");
    const [isFocused, setIsFocused] = useState(false);
    const navigate = useNavigate();

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoader(true);
        const body = {
            otp
        };
        try {
            const resp = await Calling.LoginOtp(body);
            const { status, message, token } = resp;
            if (status) {
                toast.success(message, {
                    autoClose: 2000,
                });
                onLogin(true);
                localStorage.removeItem('token');
                localStorage.setItem('admin_number_store', token);
                navigate("/");
            } else {
                toast.error(message, {
                    autoClose: 2000,
                });
            }
        } catch (error) {
            toast.error("Something went wrong. Please try again.", {
                autoClose: 2000,
            });
            navigate('/Error');
        } finally {
            setLoader(false);
        }
    };

    const handleOtpChange = (e) => {
        const value = e.target.value.replace(/[^0-9]/g, '');
        if (value.length <= 6) {
            setOtp(value);
        }
    };

    return (
        <>
            {loader && <Loader />}

            <div className="min-h-screen w-full flex items-center justify-center bg-gradient-to-br from-main-blue via-emerald-500 to-teal-600 p-4 relative overflow-hidden">
                <title>OTP Verification | Number Storage</title>

                {/* Background Decorations */}
                <div className="absolute inset-0 overflow-hidden pointer-events-none">
                    <div className="absolute -top-40 -right-40 w-80 h-80 bg-white/10 rounded-full blur-3xl"></div>
                    <div className="absolute -bottom-40 -left-40 w-96 h-96 bg-white/10 rounded-full blur-3xl"></div>
                    <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-white/5 rounded-full blur-3xl"></div>
                </div>

                {/* Main Card */}
                <div className="relative z-10 w-full max-w-md">

                    {/* Glass Card */}
                    <div className="bg-main-white rounded-3xl shadow-2xl overflow-hidden">

                        {/* Header Section with Logo */}
                        <div className="pt-10 pb-6 px-8 text-center">
                            {/* Logo */}
                            <div className="inline-flex items-center justify-center mb-5">
                                <img src={img2} alt="Logo" className="w-24 h-24 object-contain" />
                            </div>

                            {/* Title */}
                            <h1 className="text-2xl font-bold text-main-font">OTP Verification</h1>
                            <p className="text-main-subfont text-sm mt-1">Enter the 6-digit code sent to your device</p>
                        </div>

                        {/* Divider */}
                        <div className="mx-8 h-px bg-gradient-to-r from-transparent via-main-border to-transparent"></div>

                        {/* Form Section */}
                        <form onSubmit={handleSubmit} className="p-8 space-y-5">

                            {/* OTP Field */}
                            <div>
                                <label htmlFor="otp" className="block text-sm font-semibold text-main-font mb-2">
                                    Enter OTP
                                </label>
                                <div className={`relative rounded-xl border transition-all duration-200 bg-main-white ${isFocused
                                        ? 'border-main-blue shadow-md shadow-main-blue/20 ring-2 ring-main-blue/20'
                                        : 'border-main-border hover:border-main-subfont'
                                    }`}>
                                    <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                                        <div className={`w-9 h-9 rounded-lg flex items-center justify-center transition-all duration-200 ${isFocused
                                                ? 'bg-main-blue text-main-white'
                                                : 'bg-main-th text-main-subfont'
                                            }`}>
                                            <FiLock className="h-4 w-4" />
                                        </div>
                                    </div>
                                    <input
                                        type="text"
                                        id="otp"
                                        value={otp}
                                        onChange={handleOtpChange}
                                        onFocus={() => setIsFocused(true)}
                                        onBlur={() => setIsFocused(false)}
                                        className="w-full pl-16 pr-4 py-4 bg-transparent text-main-font placeholder-main-subfont focus:outline-none text-sm rounded-xl tracking-[0.5em] font-semibold text-center"
                                        placeholder="• • • • • •"
                                        maxLength="6"
                                        inputMode="numeric"
                                        autoComplete="one-time-code"
                                        required
                                    />
                                </div>

                                {/* OTP Progress Indicator */}
                                <div className="flex items-center justify-center gap-2 mt-4">
                                    {[...Array(6)].map((_, index) => (
                                        <div
                                            key={index}
                                            className={`w-3 h-3 rounded-full transition-all duration-200 ${index < otp.length
                                                    ? 'bg-main-blue scale-110'
                                                    : 'bg-main-border'
                                                }`}
                                        />
                                    ))}
                                </div>
                            </div>

                            {/* Verify Button */}
                            <button
                                type="submit"
                                disabled={loader || otp.length !== 6}
                                className="w-full mt-3 bg-main-blue text-main-white py-4 px-6 rounded-xl font-semibold hover:bg-emerald-600 hover:shadow-lg hover:shadow-main-blue/30 focus:outline-none focus:ring-2 focus:ring-main-blue focus:ring-offset-2 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                            >
                                {loader ? (
                                    <>
                                        <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24">
                                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                                        </svg>
                                        <span>Verifying...</span>
                                    </>
                                ) : (
                                    <>
                                        <span>Verify OTP</span>
                                        <FiArrowRight className="w-5 h-5" />
                                    </>
                                )}
                            </button>


                            <div className="text-center">
                                {/* Footer outside card */}
                                <p className="text-center  text-xs mt-6">
                                    © {new Date().getFullYear()} Number Storage. All rights reserved.
                                </p>
                            </div>
                        </form>


                    </div>


                </div>
            </div>
        </>
    );
}